import {
  ComponentType,
  FC,
  Fragment,
  HTMLProps,
  memo,
  ReactNode,
  SVGAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import AcceptConflict from '@/assets/icons/AcceptConflict';
import Clock from '@/assets/icons/Clock';
import Koppel from '@/assets/icons/KoppelConflict';
import Perronlengte from '@/assets/icons/Perronlengte';
import Plaatsconflict from '@/assets/icons/Plaatsconflict';
import TrainManco from '@/assets/icons/TrainManco';
import VerkeerdMat from '@/assets/icons/VerkeerdMat';
import Versterken from '@/assets/icons/Versterken';
import { useHasPermission } from '@/auth';
import { searchCriteriaForIds } from '@/components/search/v1/utils/helpers';
import useSidebarStore from '@/components/sidebar/hooks/useSidebarStore';
import {
  conflictSoortOmschrijving,
  conflictSoortTooltip,
  conflictTypeIcon,
  filterEnSorteerConflicten,
  geefDienstregelpuntVoorConflict,
  geefInzetVoorConflict,
  geefNietGeaccepteerdeElementen,
  geefStellenOfOorspronkelijkDienstVoorConflict,
  geefTijdstipVoorConflict,
  geefVerkeersdagTijdstipVoorElement,
  geefVerstoringspuntenVoorConflict,
  isVerkeersdagVandaag,
  titleInfo,
} from '@/domain/conflict/conflictUtils';
import { Conflict } from '@/types/Conflicten';
import { dayOfWeekWithTime } from '@/utils/dateUtils';
import { createContextMenuData } from '@/utils/helpers';
import { useVirtualizer } from '@tanstack/react-virtual';

import type { ConflictenFilter } from './conflictenFilter/conflictenFilterModel';
import ConflictToelichting from './ConflictToelichting';

import styles from './Conflicten.module.css';

const conflictIconMap: Record<string, ComponentType<SVGAttributes<SVGSVGElement>>> = {
  TrainManco,
  Plaatsconflict,
  VerkeerdMat,
  Versterken,
  Perronlengte,
  Koppel,
  Clock,
};

const ConflictenTable: FC<
  {
    conflicten: Conflict[];
    panelMenuInformation;
  } & HTMLProps<HTMLDivElement>
> = ({ conflicten, panelMenuInformation, ...rest }) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const [selectedConflicten, setSelectedConflicten] = useState<Conflict[]>([]);
  const activePanel = useSidebarStore((state) => state.activePanel);
  const setActivePanel = useSidebarStore((state) => state.setActivePanel);
  const setData = useSidebarStore((state) => state.setData);
  const isAllowedToInregelingen = useHasPermission('mwd.inregelingen');

  function handleOnConflictClick(event: React.MouseEvent<HTMLDivElement>, conflict: Conflict) {
    panelMenuInformation.setActivePanel('Conflicten');
    setActivePanel('Conflicten');
    event.preventDefault();
    if (event.button === 2) {
      let conflictenVoorMwd: Conflict[];
      if (!selectedConflicten.includes(conflict)) {
        setSelectedConflicten([conflict]);
        conflictenVoorMwd = [conflict];
      } else {
        conflictenVoorMwd = selectedConflicten;
      }

      const conflictType: {
        conflictType:
          | 'GEACCEPTEERD'
          | 'PLAATS'
          | 'MANCO'
          | 'VERKEERD_MATERIEEL'
          | 'VERSTERKT_MATERIEEL'
          | 'TIJD'
          | 'PERRONLENGTE'
          | 'KOPPEL';
        inzetId: string | null;
        werklijnId: string;
        isAllowedToInregelingen: boolean;
      }[] = conflictenVoorMwd.map((value) => ({
        conflictType: value.type,
        inzetId:
          geefNietGeaccepteerdeElementen(value)[0]?.werklijnElementId ?? value.werklijnElementIdLaatsteElement ?? null,
        werklijnId: value.werklijnId,
        panelType: 'conflicten',
        isAllowedToInregelingen,
      }));

      const contextMenuData = createContextMenuData(
        event,
        [
          searchCriteriaForIds(
            conflictenVoorMwd.flatMap((value) =>
              value.betrokkenWerklijnen ? value.betrokkenWerklijnen.map((b) => b.werklijnId) : [value.werklijnId],
            ),
          ),
        ],
        undefined,
        conflictType,
      );

      panelMenuInformation.contextMenuInformationCallback(contextMenuData);
    } else if (event.ctrlKey) {
      setSelectedConflicten((prev) => {
        if (prev.includes(conflict)) {
          return prev.filter((id) => id !== conflict);
        }
        return [...prev, conflict];
      });
    } else if (event.shiftKey) {
      const selectedConflictIndex = conflicten.findIndex((d) => d.werklijnId === selectedConflicten[0].werklijnId);
      const clickedConflictIndex = conflicten.findIndex((d) => d.werklijnId === conflict.werklijnId);
      const start = Math.min(selectedConflictIndex, clickedConflictIndex);
      const end = Math.max(selectedConflictIndex, clickedConflictIndex);
      setSelectedConflicten(conflicten.slice(start, end + 1));
    } else if (selectedConflicten.includes(conflict)) {
      setSelectedConflicten([]);
    } else {
      setSelectedConflicten([conflict]);
    }
  }

  function handleOnConflictKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    panelMenuInformation.setActivePanel('Conflicten');
    setActivePanel('Conflicten');
    event.preventDefault();
    if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
      setSelectedConflicten(conflicten);
    }
    if (event.altKey && event.code === 'KeyT') {
      panelMenuInformation.handleSelectieUitPanel([
        searchCriteriaForIds(selectedConflicten.map((value) => value.werklijnId)),
      ]);
      panelMenuInformation.setActivePanel(undefined);
    }
  }

  useEffect(() => {
    setData('conflicten', selectedConflicten);
  }, [selectedConflicten]);

  useEffect(() => {
    if (panelMenuInformation.activePanel !== 'Conflicten' || activePanel !== 'Conflicten') {
      setSelectedConflicten([]);
    }
  }, [panelMenuInformation.activePanel, activePanel]);

  const rowVirtualizer = useVirtualizer({
    count: conflicten.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
    overscan: 20,
  });

  return (
    conflicten && (
      <div
        ref={parentRef}
        className={styles.conflictenScrollableStyle}
        data-testid='conflictenLijst'
        {...rest}
        data-total-size={rowVirtualizer.getTotalSize()}
      >
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualItem) => (
            <div
              key={virtualItem.key}
              tabIndex={0}
              className={styles.conflictRowStyle}
              onClick={(event) => handleOnConflictClick(event, conflicten[virtualItem.index])}
              onContextMenu={(event) => handleOnConflictClick(event, conflicten[virtualItem.index])}
              onKeyDown={(event) => handleOnConflictKeyDown(event)}
              data-testid={`conflict_${conflicten[virtualItem.index].werklijnId}_${virtualItem.index}`}
              data-dtname='conflictenlijst-element'
              data-selected={selectedConflicten.includes(conflicten[virtualItem.index])}
              style={{
                position: 'absolute',
                userSelect: 'none',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
                background:
                  ((panelMenuInformation && panelMenuInformation.activePanel === 'Conflicten') ||
                    activePanel === 'Conflicten') &&
                  selectedConflicten.some(
                    (value) =>
                      value.werklijnId === conflicten[virtualItem.index].werklijnId &&
                      value.type === conflicten[virtualItem.index].type,
                  )
                    ? 'var(--tractie-color-background-selected-default)'
                    : '',
                cursor: 'pointer',
              }}
              title={titleInfo(conflicten[virtualItem.index])}
            >
              {(() => {
                const ConflictIconComp = conflictIconMap[conflictTypeIcon(conflicten[virtualItem.index].type)];
                return ConflictIconComp ? (
                  <ConflictIconComp
                    className={styles.conflictenIconStyle}
                    aria-hidden='true'
                    width='24px'
                    height='24px'
                  />
                ) : null;
              })()}
              <span
                className={styles.conflictSoortStyle}
                title={conflictSoortTooltip(conflicten[virtualItem.index].type)}
              >
                {conflictSoortOmschrijving(conflicten[virtualItem.index].type)}
              </span>
              {conflicten[virtualItem.index].allElementsAccepted && (
                <span style={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'center' }}>
                  <AcceptConflict width={16} height={16} fill={'var(--tractie-color-content-emphasis'} />
                </span>
              )}
              <span className={styles.eenheidSpanStyle}>
                {conflicten[virtualItem.index].allElementsAccepted &&
                conflicten[virtualItem.index].lastAcceptedPerDay?.length
                  ? conflicten[virtualItem.index].lastAcceptedPerDay!.map((el, index) => (
                      <Fragment key={el.treinNummer}>
                        {index > 0 && ' - '}
                        {el.uitvoeringstijd && !isVerkeersdagVandaag(el) && (
                          <span style={{ fontWeight: 300, textTransform: 'lowercase' }}>
                            {geefVerkeersdagTijdstipVoorElement(el)}:{' '}
                          </span>
                        )}
                        {conflicten[virtualItem.index].type === 'MANCO'
                          ? el.oorspronkelijkeDienst
                          : geefStellenOfOorspronkelijkDienstVoorConflict(conflicten[virtualItem.index])}
                      </Fragment>
                    ))
                  : geefStellenOfOorspronkelijkDienstVoorConflict(conflicten[virtualItem.index])}
              </span>

              <ConflictToelichting conflict={conflicten[virtualItem.index]} />
              <span className={styles.inzetStyle}>
                {geefInzetVoorConflict(conflicten[virtualItem.index]) as ReactNode}
              </span>
              <span className={styles.drglptSpanStyle}>
                {geefDienstregelpuntVoorConflict(conflicten[virtualItem.index])}
              </span>

              <span
                className={
                  conflicten[virtualItem.index].allElementsAccepted
                    ? styles.verstoringspuntenVolledigGeaccepteerd
                    : styles.verstoringspuntenSpanStyle
                }
              >
                {geefVerstoringspuntenVoorConflict(conflicten[virtualItem.index])}
              </span>

              <div className={styles.timeDivStyle}>
                <span className={styles.timeSpanStyle}>
                  {dayOfWeekWithTime(geefTijdstipVoorConflict(conflicten[virtualItem.index]))}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  );
};

const MemoizedConflictenTable = memo(ConflictenTable);

const ConflictenLijst: FC<
  {
    conflicten: Conflict[];
    filter: ConflictenFilter;
    panelMenuInformation;
  } & HTMLProps<HTMLDivElement>
> = ({ conflicten, filter, panelMenuInformation, ...rest }) => {
  const gefilterdeData = useMemo(() => filterEnSorteerConflicten(conflicten, filter), [conflicten, filter]);

  return <MemoizedConflictenTable conflicten={gefilterdeData} {...rest} panelMenuInformation={panelMenuInformation} />;
};

export default ConflictenLijst;
